import type {
  BookedListItem,
  CustomBookingListRow,
} from '@/state/booked-classes';
import type { LiveWaitlistEntry } from '@/services/waitlist';
import type { GuestOf } from '@/lib/types';
import { getOccurrenceId, orderReceiptPath } from '@/helpers/order';
import type { WaitlistSlotKey } from '@/helpers/waitlist';
import { formatInTimeZone } from '@/lib/utils';

const isCustomRow = (row: BookedListItem): row is CustomBookingListRow =>
  'is_custom' in row && row.is_custom === true;

export type BookedClassCardItem = {
  href: string;
  name: string;
  image: string | null;
  startTime: string;
  endTime: string;
  quantity: number;
  instructor: { first_name: string; last_name: string | null } | null;
  substitute: { first_name: string; last_name: string } | null;
  // The host, on a booking she came to as their guest.
  guestOf?: GuestOf | null;
  // The guests she brought to this class time.
  guestCount?: number;
  waitlist?: Pick<
    LiveWaitlistEntry,
    'status' | 'position' | 'offer_expires_at'
  >;
};

export type BookedClassGroup = {
  date: string;
  dayName: string;
  dateFormatted: string;
  classes: BookedClassCardItem[];
};

// One row per class time of an order — items share the class time, class and
// instructor, and the spots count conveys quantity; per-item rows would
// duplicate.
export const toBookedClassCardItem = (
  row: BookedListItem
): BookedClassCardItem | null => {
  if (isCustomRow(row)) {
    return {
      href: `/orders/${row.ref}?type=custom`,
      name: row.name,
      image: null,
      startTime: row.start_time,
      endTime: row.end_time,
      quantity: 1,
      instructor: row.instructor,
      substitute: null,
    };
  }

  const item = row.class_order_items[0];
  if (!item) return null;

  return {
    href: orderReceiptPath(row.order_id, null, {
      slotId: getOccurrenceId(item),
      startTime: item.start_time,
    }),
    name: item.class.name,
    image: item.class.image,
    startTime: item.start_time,
    endTime: item.end_time,
    quantity: row.active_quantity ?? 1,
    instructor: item.instructor_schedule.admin_user,
    substitute: item.substitute_admin_user ?? null,
    guestOf: row.guest_of ?? null,
    guestCount: row.guest_count,
  };
};

// Her list holds live bookings only. A weekly slot repeats, so the start
// instant pins the date, and after a class move her bookings carry the new
// slot ids the grid shows.
export const bookingReceiptPathFor = (
  rows: BookedListItem[] | undefined,
  key: WaitlistSlotKey
): string | null => {
  if (!key.slot_id && !key.override_slot_id) return null;
  const startMs = new Date(key.start_time).getTime();
  for (const row of rows ?? []) {
    if (isCustomRow(row)) continue;
    const item = row.class_order_items.find(
      (candidate) =>
        (key.slot_id
          ? candidate.slot_id === key.slot_id
          : candidate.override_slot_id === key.override_slot_id) &&
        new Date(candidate.start_time).getTime() === startMs
    );
    if (item) {
      return orderReceiptPath(row.order_id, null, {
        slotId: getOccurrenceId(item),
        startTime: item.start_time,
      });
    }
  }
  return null;
};

export const toWaitlistCardItem = (
  entry: LiveWaitlistEntry
): BookedClassCardItem => ({
  href: `/waitlist/${entry.waitlist_entry_id}`,
  name: entry.class?.name ?? 'Class',
  image: entry.class?.image ?? null,
  startTime: entry.occurrence.start_time,
  endTime: entry.occurrence.end_time,
  quantity: 1,
  instructor: entry.instructor,
  substitute: null,
  waitlist: {
    status: entry.status,
    position: entry.position,
    offer_expires_at: entry.offer_expires_at,
  },
});

// Grouped by the device's calendar day, the zone every class time is shown in.
export const groupBookedClassesByDate = (
  items: BookedClassCardItem[]
): BookedClassGroup[] => {
  const groups: BookedClassGroup[] = [];

  for (const item of items) {
    const start = new Date(item.startTime);
    const dateKey = formatInTimeZone(
      start,
      undefined,
      { year: 'numeric', month: '2-digit', day: '2-digit' },
      'en-CA'
    );
    const existing = groups.find((g) => g.date === dateKey);
    if (existing) {
      existing.classes.push(item);
    } else {
      groups.push({
        date: dateKey,
        dayName: formatInTimeZone(
          start,
          undefined,
          { weekday: 'short' },
          'en-GB'
        ),
        dateFormatted: formatInTimeZone(
          start,
          undefined,
          { day: 'numeric', month: 'short' },
          'en-GB'
        ),
        classes: [item],
      });
    }
  }

  // Waitlist rows interleave chronologically with bookings inside each day.
  for (const group of groups) {
    group.classes.sort((a, b) => a.startTime.localeCompare(b.startTime));
  }

  return groups.sort((a, b) => a.date.localeCompare(b.date));
};
